import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { fetchFastApi } from "../api/fastapi";
import {
  ListOptroControlsResponseSchema,
  OptroControlSummary,
  OptroFilterOptions,
  OptroFilterOptionsSchema,
} from "../models/audit/optroSync";
import { useDebounce } from "./useDebounce";

export const OPTRO_IMPORT_PAGE_SIZE = 50;

export interface OptroImportControlsFilters {
  processId: number | null;
  ownerUserId: number | null;
  excludeLinked: boolean;
}

// Lists Optro controls for the import wizard's select step, with backend
// pagination, server-side search, and process/owner/not-imported filters.
// Exposes `loadMore` / `hasMore` for the select page's infinite scroll,
// mirroring the accumulate-and-append shape of `useControlsV2`.
//
// Pagination is driven by the server's `nextOffset` / `hasMore` rather than
// derived from `total` / item count: when `excludeLinked` is set the backend
// refills pages by skipping ahead past linked Optro rows, so the number
// of rows returned no longer maps 1:1 onto Optro's own offset.
function buildListParams(
  search: string,
  filters: OptroImportControlsFilters,
  offset: number,
  limit: number,
): URLSearchParams {
  const params = new URLSearchParams({
    search,
    limit: String(limit),
    offset: String(offset),
    exclude_linked: String(filters.excludeLinked),
  });
  if (filters.processId !== null) {
    params.set("process_id", String(filters.processId));
  }
  if (filters.ownerUserId !== null) {
    params.set("owner_user_id", String(filters.ownerUserId));
  }
  return params;
}

export function useOptroImportControls(
  search: string,
  filters: OptroImportControlsFilters,
) {
  const debouncedSearch = useDebounce(search, 300);
  const { processId, ownerUserId, excludeLinked } = filters;

  const [controls, setControls] = useState<OptroControlSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // False until the very first fetch settles. Lets the page distinguish the
  // first-ever load (full interstitial) from a search/filter refetch (dimmed
  // table + spinner overlay) — without this, clearing a search that matched
  // nothing looked like a first load again.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  // The Optro offset the next page should start from, as reported by the
  // server. Can't be derived from `controls.length` because `excludeLinked`
  // refills a page across multiple Optro pages server-side.
  const nextOffsetRef = useRef(0);
  // Incremented on every reset fetch; used to discard results from a reset
  // fetch that has since been superseded by a newer search term or filter
  // change, so a slow in-flight `loadMore` response can't append stale rows
  // onto the new list or stomp `nextOffsetRef`.
  const generationRef = useRef(0);

  // Treat unmount as a superseding event: bumping the generation makes every
  // in-flight fetch's stale checks discard its response instead of setting
  // state on the unmounted hook — the same protection the `cancelled` flag
  // gives `useOptroFilterOptions` below.
  useEffect(() => {
    return () => {
      generationRef.current += 1;
    };
  }, []);

  const fetchPage = useCallback(
    async (offset: number, replace: boolean) => {
      if (replace) {
        generationRef.current += 1;
      }
      const generation = generationRef.current;

      if (replace) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(false);
      try {
        const params = buildListParams(
          debouncedSearch,
          { processId, ownerUserId, excludeLinked },
          offset,
          OPTRO_IMPORT_PAGE_SIZE,
        );

        const response = await fetchFastApi({
          method: "GET",
          path: `/controls/external/controls?${params.toString()}`,
        });
        // Discard results from a superseded reset fetch.
        if (generationRef.current !== generation) return;
        const parsed = ListOptroControlsResponseSchema.parse(response);
        setControls((prev) =>
          replace ? parsed.controls : [...prev, ...parsed.controls],
        );
        setTotal(parsed.total);
        setHasMore(parsed.hasMore);
        nextOffsetRef.current = parsed.nextOffset;
      } catch (err) {
        if (generationRef.current !== generation) return;
        console.error("Failed to load Optro controls:", err);
        setError(true);
      } finally {
        if (replace) {
          // Only clear `loading` if a newer reset hasn't already taken
          // over; otherwise this stale call would flip it back to false
          // while the newer reset is still in flight.
          if (generationRef.current === generation) {
            setLoading(false);
            setHasLoadedOnce(true);
          }
        } else {
          setLoadingMore(false);
        }
      }
    },
    [debouncedSearch, processId, ownerUserId, excludeLinked],
  );

  useEffect(() => {
    nextOffsetRef.current = 0;
    void fetchPage(0, true);
  }, [fetchPage, attempt]);

  const loadMore = useCallback(() => {
    if (!loading && !loadingMore && hasMore) {
      void fetchPage(nextOffsetRef.current, false);
    }
  }, [loading, loadingMore, hasMore, fetchPage]);

  // Refs mirroring state that fetchAllRemaining's loop needs current values
  // of without re-creating the callback per render.
  const controlsRef = useRef<OptroControlSummary[]>([]);
  const hasMoreRef = useRef(false);
  useEffect(() => {
    controlsRef.current = controls;
  }, [controls]);
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  // Fetches every remaining page for the current search/filters (used by the
  // header select-all) and returns the COMPLETE matching list — already-loaded
  // rows plus everything fetched here. Pages land in `controls` as they
  // arrive, so the table fills up alongside the selection. Returns
  // "superseded" (silently) when a search/filter change invalidates the loop
  // mid-flight, or "error" if a page fails.
  const fetchAllRemaining = useCallback(async (): Promise<
    | { status: "ok"; controls: OptroControlSummary[] }
    | { status: "superseded" }
    | { status: "error" }
  > => {
    const generation = generationRef.current;
    const all = [...controlsRef.current];
    if (!hasMoreRef.current) {
      return { status: "ok", controls: all };
    }

    // Use the listing endpoint's maximum page size so select-all drains in
    // the fewest requests.
    const SELECT_ALL_PAGE_SIZE = 100;
    const MAX_PAGES = 200; // safety backstop; 20k rows is beyond any real tenant
    let offset = nextOffsetRef.current;
    setLoadingMore(true);
    try {
      for (let page = 0; page < MAX_PAGES; page++) {
        const params = buildListParams(
          debouncedSearch,
          { processId, ownerUserId, excludeLinked },
          offset,
          SELECT_ALL_PAGE_SIZE,
        );

        const response = await fetchFastApi({
          method: "GET",
          path: `/controls/external/controls?${params.toString()}`,
        });
        if (generationRef.current !== generation) {
          return { status: "superseded" };
        }
        const parsed = ListOptroControlsResponseSchema.parse(response);
        all.push(...parsed.controls);
        offset = parsed.nextOffset;
        nextOffsetRef.current = parsed.nextOffset;
        setControls([...all]);
        setTotal(parsed.total);
        setHasMore(parsed.hasMore);
        if (!parsed.hasMore) {
          return { status: "ok", controls: all };
        }
      }
      // MAX_PAGES exhausted without the server reporting the end.
      console.error(
        "Select-all aborted: page cap reached before hasMore=false",
      );
      return { status: "error" };
    } catch (err) {
      if (generationRef.current !== generation) {
        return { status: "superseded" };
      }
      console.error("Failed to load all matching Optro controls:", err);
      return { status: "error" };
    } finally {
      if (generationRef.current === generation) {
        setLoadingMore(false);
      }
    }
  }, [debouncedSearch, processId, ownerUserId, excludeLinked]);

  return {
    controls,
    total,
    loading,
    loadingMore,
    hasLoadedOnce,
    error,
    retry: () => setAttempt((n) => n + 1),
    loadMore,
    hasMore,
    fetchAllRemaining,
  };
}

// Fetches the process/owner filter options for the Optro import select
// step's filters, once on mount. On failure, surfaces a toast and leaves
// `options` null so the select page can render its filters disabled rather
// than block the whole page.
export function useOptroFilterOptions() {
  const [options, setOptions] = useState<OptroFilterOptions | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchOptions = async () => {
      setLoading(true);
      try {
        const response = await fetchFastApi({
          method: "GET",
          path: "/controls/external/filter-options",
        });
        if (cancelled) return;
        setOptions(OptroFilterOptionsSchema.parse(response));
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load Optro filter options:", err);
        toast.error("Could not load process/owner filters.");
        setOptions(null);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchOptions();

    return () => {
      cancelled = true;
    };
  }, []);

  return { options, loading };
}
